import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

import BusinessRequestIcon from "../icons/BusinessHub/BusinessRequestIcon";
import "../styles/BusinessReq.css";
import GloablButton from "./GloablButton";
import EyeViewOpened from "../icons/BusinessHub/EyeViewOpened";
import ResubmitIcon from "../icons/BusinessHub/ResubmitIcon";
import RejectedIcon from "../icons/BusinessHub/RejectedIcon";
import Approved from "../icons/BusinessHub/ApprovedIcon";
import ApprovedBigIcon from "../icons/BusinessHub/BigApprovedIcon";
import CyrcleIcon from "../icons/BusinessHub/cyrcleIcon";
import ApplicantIcon from "../icons/BusinessHub/ApplicantInformationIcon";
import InternalNotesIcon from "../icons/BusinessHub/InternalNotesIcon";
import ReviewActiveLog from "../icons/BusinessHub/ReviewActivityLogIcon";
import FlagIcon from "../icons/BusinessHub/FlagIcon";
import CyrcleIconRed from "../icons/BusinessHub/cyrcleExclamationIconRed";
import MessageIcon from "../icons/BusinessHub/MessageIcon";
import TrachIcon from "../icons/SignInIcon/TrachIcon";
import Warnig from "../icons/BusinessHub/warningExlamationIcon";
import FaulseIcon from "../icons/BusinessHub/FaulseIcon";
import DocumentsVerificationIcon from "../icons/BusinessHub/DocumentsVerificationIcon";
import Exclamation from "../icons/BusinessHub/exclamationIcon";
import ArrowBackIcon from "../icons/BusinessHub/ArrowBackIcon.js";
import ArrowFront from "../icons/BusinessHub/ArrowFront.js";
import DateIcon from "../icons/BusinessHub/DateIcon.js";

import companyController from "../controllers/companyController";
import Utils from "../utils/Utils";
import { userService } from "../services/userService";
import InlineLogoLoader from "./InlineLogoLoader";

const asText = (v) => {
  if (v === null || v === undefined) return "-";
  if (typeof v === "string" || typeof v === "number") return String(v);
  if (typeof v === "object") {
    return (
      v?.name ||
      v?.countryName ||
      v?.country_name ||
      v?.label ||
      v?.value ||
      v?.code ||
      "-"
    );
  }
  return String(v);
};

const getJwtString = () => {
  const raw = userService.getUser?.() ?? userService.getUser?.();
  if (!raw) return "";
  if (typeof raw === "string") return raw;
  return raw?.jwt ?? raw?.token ?? "";
};

const getCompanyIdsFromJwt = (jwt) => {
  try {
    const parsed = Utils.parseJwt(jwt);
    const companyId = Object.keys(parsed?.userDetails || {})[0] || null;
    const details = companyId ? parsed?.userDetails?.[companyId] : null;

    const businessProfileID =
      details?.businessProfileID ??
      details?.businessProfileId ??
      details?.business_profile_id ??
      details?.busienssProfileID ??
      null;

    return { companyId, businessProfileID };
  } catch (e) {
    return { companyId: null, businessProfileID: null };
  }
};

const getReviewerFromJwt = (jwt) => {
  try {
    const p = Utils.parseJwt(jwt) || {};
    const first =
      p?.firstName || p?.firstname || p?.given_name || p?.givenName || "";
    const last =
      p?.lastName || p?.lastname || p?.family_name || p?.familyName || "";
    const full = `${first} ${last}`.trim();
    return full || p?.name || p?.username || p?.sub || "Unassigned";
  } catch (e) {
    return "Unassigned";
  }
};

const pickCompanyIdFromRow = (row) => {
  const r = row?.raw || {};
  return (
    r?.companyID ||
    r?.companyId ||
    r?.companyUuid ||
    r?.companyUUID ||
    r?.company?.id ||
    r?.company?.uuid ||
    null
  );
};

const pickRequestIdFromRow = (row) => {
  const r = row?.raw || {};
  return r?.requestId || r?.requestID || r?.id || r?.uuid || null;
};

const isSyntheticRequestId = (v) => {
  const s = String(v || "").trim();
  return /^REQ-\d+-\d+$/i.test(s);
};

const normalizeRequestId = (v) => {
  const s = String(v || "").trim();
  if (!s) return null;
  if (isSyntheticRequestId(s)) return null;
  return s;
};

const mapUiTypeToApi = (t) => {
  const v = String(t || "")
    .trim()
    .toLowerCase();
  if (v === "vendor") return "VENDOR";
  if (v === "delivery company") return "DELIVERY_COMPANY";
  if (v === "independent driver") return "INDEVIDUAL_DRIVER";
  if (v === "affiliate program") return "END_USER";
  if (v === "technicians") return "SYSTEM";
  return "";
};

const mapUiStatusToApi = (s) => {
  const v = String(s || "")
    .trim()
    .toLowerCase();
  if (v === "pending") return "PENDING";
  if (v === "approved") return "APPROVED";
  if (v === "rejected") return "REJECTED";
  if (v === "resubmit") return "RESUBMIT";
  return "";
};

const normalizeStatusToUi = (s) => {
  const v = String(s || "")
    .trim()
    .toUpperCase();
  if (v === "PENDING") return "Pending";
  if (v === "APPROVED") return "Approved";
  if (v === "REJECTED") return "Rejected";
  if (v === "RESUBMIT" || v === "RESUBMISSION") return "Resubmit";
  return s || "Pending";
};

const normalizeTypeToUi = (t) => {
  const v = String(t || "")
    .trim()
    .toUpperCase();
  if (v === "VENDOR") return "Vendor";
  if (v === "DELIVERY_COMPANY") return "Delivery Company";
  if (v === "INDEVIDUAL_DRIVER") return "Independent Driver";
  if (v === "END_USER") return "Affiliate Program";
  if (v === "SYSTEM") return "Technicians";
  return t || "Vendor";
};

const formatDateGB = (d) => {
  if (!d) return "-";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString("en-GB");
};

const pickSubmittedBy = (it) =>
  it?.submittedBy ??
  it?.submitted_by ??
  it?.submitterEmail ??
  it?.submitter_email ??
  it?.userEmail ??
  it?.email ??
  it?.applicantEmail ??
  it?.applicant_email ??
  it?.createdByEmail ??
  it?.created_by_email ??
  "-";

const pickCountry = (it) =>
  asText(
    it?.country ??
      it?.countryName ??
      it?.country_name ??
      it?.countryOfResidence ??
      it?.country_of_residence ??
      it?.residenceCountry ??
      it?.residenceCountryName ??
      it?.residence_country ??
      it?.residence_country_name ??
      it?.companyCountry ??
      it?.company_country ??
      it?.company?.country ??
      it?.company?.countryName ??
      it?.owner?.country ??
      it?.owner?.countryName ??
      it?.address?.country ??
      it?.address?.countryName ??
      it?.countryCode ??
      it?.country_code,
  );

const pickCountryFromDetails = (d) => {
  return asText(
    d?.countryName ||
      d?.country ||
      d?.country_name ||
      d?.countryOfResidence ||
      d?.country_of_residence ||
      d?.residenceCountryName ||
      d?.residenceCountry ||
      d?.residence_country_name ||
      d?.residence_country ||
      d?.companyCountry ||
      d?.company_country ||
      d?.company?.countryName ||
      d?.company?.country ||
      d?.owner?.countryName ||
      d?.owner?.country ||
      d?.address?.countryName ||
      d?.address?.country ||
      d?.companyAddress?.countryName ||
      d?.companyAddress?.country ||
      d?.ownerAddress?.countryName ||
      d?.ownerAddress?.country ||
      d?.countryCode ||
      d?.country_code,
  );
};

function BussinessReq() {
  const jwt = getJwtString();
  const [reqID, setreqID] = useState("");
  const typeRef = useRef(null);
  const statusRef = useRef(null);

  const [activeView, setActiveView] = useState(null);

  const [isTypeOpen, setIsTypeOpen] = useState(false);
  const [isStatusOpen, setIsStatusOpen] = useState(false);

  const [selectedType, setSelectedType] = useState("All");
  const [selectedStatus, setSelectedStatus] = useState("All");

  const [RowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);

  const [loadingList, setLoadingList] = useState(false);
  const [rows, setRows] = useState([]);
  const [selectedRowIds, setSelectedRowIds] = useState([]);


  const requestTypes = [
    "All",
    "Vendor",
    "Delivery Company",
    "Independent Driver",
    "Affiliate Program",
    "Technicians",
  ];

  const statuses = ["All", "Pending", "Approved", "Resubmit", "Rejected"];

  const closeAll = () => {
    setIsTypeOpen(false);
    setIsStatusOpen(false);
  };

  useEffect(() => {
    const onDocDown = (e) => {
      const t = typeRef.current;
      const s = statusRef.current;
      if (t && t.contains(e.target)) return;
      if (s && s.contains(e.target)) return;
      closeAll();
    };
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, []);

  useEffect(() => {
    const onEsc = (e) => {
      if (e.key === "Escape") closeAll();
    };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, []);

  const allVisibleRowsSelected =
    rows.length > 0 && rows.every((row) => selectedRowIds.includes(row.id));
  const toggleAllVisibleRows = () => {
    if (allVisibleRowsSelected) {
      setSelectedRowIds((prev) =>
        prev.filter((id) => !rows.some((row) => row.id === id)),
      );
      return;
    }

    setSelectedRowIds((prev) => {
      const visibleIds = rows.map((row) => row.id);
      return Array.from(new Set([...prev, ...visibleIds]));
    });
  };

  const toggleRowSelection = (rowId) => {
    setSelectedRowIds((prev) =>
      prev.includes(rowId)
        ? prev.filter((id) => id !== rowId)
        : [...prev, rowId],
    );
  };

  useEffect(() => {
    setSelectedRowIds((prev) =>
      prev.filter((id) => rows.some((row) => row.id === id)),
    );
  }, [rows]);

  const updateRowReviewer = (requestId, reviewerName) => {
    setRows((prev) =>
      prev.map((r) =>
        r.id === requestId ? { ...r, reviewer: reviewerName } : r,
      ),
    );

    setActiveView((prev) =>
      prev?.id === requestId ? { ...prev, reviewer: reviewerName } : prev,
    );
  };

  const saveReviewerName = async (row, reviewerName) => {
    const companyID = row?.companyId || pickCompanyIdFromRow(row);
    if (!companyID || !reviewerName || reviewerName === "Unassigned") return;
    try {
      const res = await companyController.updatecompanydetails({
        updateDetails: { companyID, reviewerName },
        jwt,
      });
      if (!Utils.isSuccessResponse(res)) {
        console.warn("updateCompnayDetails (reviewerName) failed", res);
      }
    } catch (e) {
      console.warn("updateCompnayDetails (reviewerName) error", e);
    }
  };

  const openView = (row) => {
    if (!jwt) {
      toast.error("Missing JWT token");
      return;
    }
    const reviewerName = getReviewerFromJwt(jwt);
    const now = new Date().toISOString();
    updateRowReviewer(
      row.id,
      reviewerName,
      row?.companyId || pickCompanyIdFromRow(row) || row?.requestId,
    );
    saveReviewerName(row, reviewerName);
    setActiveView({
      ...row,
      reviewer: reviewerName,
      assignedOn: row?.assignedOn || now,
      lastActivity: row?.lastActivity || now,
    });
    window.history.pushState({ view: "details" }, "");
  };

  useEffect(() => {
    const onPopState = () => setActiveView(null);
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const badgeClass = (s) => {
    const v = String(s || "").toLowerCase();
    if (v === "pending") return "br-status br-status-pending";
    if (v === "approved") return "br-status br-status-approved";
    if (v === "resubmit") return "br-status br-status-resubmit";
    if (v === "rejected") return "br-status br-status-rejected";
    return "br-status";
  };

  const handleChangeRows = (e) => {
    const next = Number(e.target.value);
    if (!Number.isFinite(next)) return;
    const clamped = Math.min(100, Math.max(1, Math.floor(next)));
    setRowsPerPage(clamped);
    setCurrentPage(1);
  };

  const getPages = () => {
    const pages = [];
    const total = totalPages;
    const current = currentPage;

    if (total <= 7) {
      for (let i = 1; i <= total; i++) pages.push(i);
      return pages;
    }

    pages.push(1);

    if (current > 3) pages.push("ellipsis");

    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);
    for (let i = start; i <= end; i++) pages.push(i);

    if (current < total - 2) pages.push("ellipsis");

    pages.push(total);

    return pages;
  };

  const selectType = (v) => {
    setSelectedType(v);
    setIsTypeOpen(false);
    setCurrentPage(1);
  };

  const selectStatus = (v) => {
    setSelectedStatus(v);
    setIsStatusOpen(false);
    setCurrentPage(1);
  };

  const updateRowStatus = (requestId, nextStatusUi) => {
    const now = new Date().toISOString();

    setRows((prev) =>
      prev.map((r) =>
        r.id === requestId
          ? { ...r, status: nextStatusUi, lastActivity: now }
          : r,
      ),
    );

    setActiveView((prev) =>
      prev?.id === requestId
        ? { ...prev, status: nextStatusUi, lastActivity: now }
        : prev,
    );
  };

  useEffect(() => {
    const fetchRequests = async () => {
      try {
        if (!jwt) {
          toast.error("Missing JWT token");
          return;
        }

        setLoadingList(true);

        const pageApi = Math.max(1, currentPage);
        const statusApi =
          selectedStatus === "All" ? "" : mapUiStatusToApi(selectedStatus);
        const userTypeApi =
          selectedType === "All" ? "" : mapUiTypeToApi(selectedType);

        const res = await companyController.getCompanyRequests({
          page: pageApi,
          size: RowsPerPage,
          status: statusApi,
          userType: userTypeApi,
          search: "",
          jwt,
        });
        const data = res?.data ?? {};
        const content =
          data?.content ?? data?.data ?? data?.items ?? data?.results ?? [];

        const totalEl =
          data?.totalElements ?? data?.total ?? data?.count ?? content.length;

        const totalPg =
          data?.totalPages ??
          data?.pages ??
          Math.max(1, Math.ceil(Number(totalEl || 0) / RowsPerPage));

        const mapped = Array.isArray(content)
          ? content.map((it, idx) => {
              const requestId = normalizeRequestId(
                it?.requestId ?? it?.requestID ?? it?.id ?? it?.uuid ?? null,
              );
              const id = requestId || `REQ-${pageApi}-${idx + 1}`;

              const applicantName =
                it?.applicantName ??
                it?.ownerName ??
                it?.name ??
                it?.fullName ??
                "-";

              const submittedBy = pickSubmittedBy(it);

              const companyName =
                it?.companyName ?? it?.businessName ?? it?.company?.name ?? "-";

              const createdAt =
                it?.date ??
                it?.submissionDate ??
                it?.createdAt ??
                it?.created_at ??
                it?.submittedAt ??
                null;

              const statusUi = normalizeStatusToUi(
                it?.requestStatus ?? it?.status,
              );
              const typeUi = normalizeTypeToUi(
                it?.requestType ?? it?.userType ?? it?.type,
              );

              const reviewerFromApi = String(
                it?.assignedReviewer ||
                  it?.reviewerName ||
                  it?.reviewer ||
                  it?.company?.reviewerName ||
                  "",
              ).trim();

              const reviewer = reviewerFromApi || "Unassigned";
              const status = statusUi;

              const companyId =
                it?.companyID ||
                it?.companyId ||
                it?.companyUuid ||
                it?.companyUUID ||
                it?.company?.id ||
                it?.company?.uuid ||
                null;

              const ownerId =
                it?.ownerId ||
                it?.ownerID ||
                it?.owner?.id ||
                it?.applicantID ||
                it?.applicantId ||
                null;

              return {
                raw: it,
                id,
                requestId,
                companyId,
                ownerId,
                type: typeUi,
                requestType:
                  it?.requestType || it?.userType || it?.type || null,
                name: applicantName,
                submittedBy,
                companyName,
                country: pickCountry(it),
                date: createdAt ? formatDateGB(createdAt) : "-",
                status,
                reviewer,
                assignedOn:
                  it?.assignedOn || it?.assigned_on || createdAt || null,
                lastActivity: it?.lastActivity || it?.last_activity || null,
              };
            })
          : [];

        const filteredRows = mapped.filter((r) => {
          const statusOk =
            selectedStatus === "All" || String(r.status) === selectedStatus;
          const typeOk =
            selectedType === "All" || String(r.type) === selectedType;
          return statusOk && typeOk;
        });

        setRows(filteredRows);
        setTotalElements(
          selectedStatus === "All" && selectedType === "All"
            ? Number(totalEl || 0)
            : filteredRows.length,
        );
        setTotalPages(
          selectedStatus === "All" && selectedType === "All"
            ? Math.max(1, Number(totalPg || 1))
            : Math.max(
                1,
                Math.ceil(filteredRows.length / Math.max(1, RowsPerPage)),
              ),
        );
      } catch (e) {
        toast.error(e?.response?.data?.message || "Error loading requests");
      } finally {
        setLoadingList(false);
      }
    };

    if (!activeView) fetchRequests();
  }, [
    jwt,
    currentPage,
    RowsPerPage,
    selectedType,
    selectedStatus,
    activeView,
  ]);

  const shownCount = rows.length;

  return (
    <div className="business-request-wrapper">
      <div className="business-request-main-container">
        {!activeView && (
          <div className="business-request-top">
            <div className="business-request-left">
              <div className="business-request-icon">
                <BusinessRequestIcon />
              </div>

              <div className="business-request-text">
                <div className="business-request-main-title">
                  Business Requests
                </div>
                <div className="business-request-main-paragraphe">
                  Review and verify onboarding requests from vendors, delivery
                  companies, drivers, and affiliates.
                </div>
              </div>
            </div>

            <div className="business-request-action">
              <GloablButton
                width="130px"
                height="44px"
                backgroundColor="#FFFFFF"
                text={`${totalElements} Requests`}
                borderRadius="50px"
                color="#111827"
                padding="11.0527px 20px"
                boxShadow="inset 0px -1px 3px rgba(0, 18, 71, 0.5)"
              />
            </div>
          </div>
        )}

        {!activeView ? (
          <div className="business-request-box">
            <div className="business-request-box-header">
              <div className="business-request-box-container">
                <div className="title-box">Incoming Requests</div>
                <div className="paragraphe-box">
                  Showing {shownCount} requests
                </div>
              </div>

              <div className="business-request-box-actions">
                <div className="br-dd" ref={typeRef}>
                  <div
                    className={`br-filter-btn ${isTypeOpen ? "br-open" : ""}`}
                    onClick={() => {
                      setIsTypeOpen((p) => !p);
                      setIsStatusOpen(false);
                    }}
                  >
                    <div className="br-filter-content">
                      <div className="br-filter-toprow">
                        <span className="br-filter-title">
                          {selectedType === "All"
                            ? "Request Type"
                            : selectedType}
                        </span>
                        <span className="br-caret">
                          {isTypeOpen ? "▴" : "▾"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {isTypeOpen && (
                    <div
                      className="br-menu br-menu-type"
                      style={{ position: "absolute" }}
                    >
                      {requestTypes.map((t) => (
                        <div
                          key={t}
                          className={`br-item ${
                            selectedType === t ? "br-item-active" : ""
                          }`}
                          onClick={() => selectType(t)}
                        >
                          {t}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="br-dd" ref={statusRef}>
                  <div
                    className={`br-filter-btn-status ${
                      isStatusOpen ? "br-open" : ""
                    }`}
                    onClick={() => {
                      setIsStatusOpen((p) => !p);
                      setIsTypeOpen(false);
                    }}
                  >
                    <div className="br-filter-content">
                      <div className="br-filter-toprow">
                        <span className="br-filter-title">
                          {selectedStatus === "All" ? "Status" : selectedStatus}
                        </span>
                        <span className="br-caret">
                          {isStatusOpen ? "▴" : "▾"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {isStatusOpen && (
                    <div
                      className="br-menu br-menu-status"
                      style={{ position: "absolute" }}
                    >
                      {statuses.map((s) => (
                        <div
                          key={s}
                          className={`br-item ${
                            selectedStatus === s ? "br-item-active" : ""
                          }`}
                          onClick={() => selectStatus(s)}
                        >
                          {s}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="business-request-box-body">
              <div className="br-table-wrap">
                <div className="br-list-table">
                  <div className="br-list-header">
                    <div className="br-list-header-request-group">
                      <div className="br-list-header-check">
                        <input
                          className="br-checkbox"
                          type="checkbox"
                          checked={allVisibleRowsSelected}
                          onChange={toggleAllVisibleRows}
                        />
                      </div>
                      <div className="br-list-header-request">Request ID</div>
                    </div>
                    <div className="br-list-header-type br-list-header-company">
                      Company Name
                    </div>
                    <div className="br-list-header-type">Type</div>
                    <div className="br-list-header-applicant">
                      Applicant Name
                    </div>
                    <div className="br-list-header-submitted">Submitted By</div>
                    <div className="br-list-header-date">Submission Date</div>
                    <div className="br-list-header-status">Status</div>
                    <div className="br-list-header-reviewer">
                      Assigned Reviewer
                    </div>
                    <div className="br-list-header-actions">Actions</div>
                  </div>

                  {rows.map((r) => (
                    <div className="br-list-row br-mobile-card-row" key={r.id}>
                      <div className="br-list-row-request-group br-mobile-card-request-group">
                        <div className="br-list-row-check">
                          <input
                            className="br-checkbox"
                            type="checkbox"
                            checked={selectedRowIds.includes(r.id)}
                            onChange={() => toggleRowSelection(r.id)}
                          />
                        </div>
                        <div className="br-list-row-request br-mobile-card-request">
                          {r.companyId || "-"}
                        </div>
                      </div>{" "}
                      <div className="br-list-row-type br-list-row-company br-mobile-card-company">
                        <span className="br-mobile-card-type-chip">
                          {r.companyName}
                        </span>
                      </div>
                      <div className="br-list-row-type br-mobile-card-type">
                        <span className="br-mobile-card-type-chip">
                          {r.type}
                        </span>
                      </div>
                      <div className="br-list-row-applicant br-mobile-card-applicant">
                        {r.name}
                      </div>
                      <div className="br-list-row-submitted br-mobile-card-submitted">
                        {r.submittedBy}
                      </div>
                      <div className="br-list-row-date br-mobile-card-date">
                        <span className="br-mobile-card-meta-icon">
                          <DateIcon strokeWidth="1.2" />
                        </span>
                        <span>{r.date}</span>
                      </div>
                      <div className="br-list-row-status br-mobile-card-status">
                        <span className={badgeClass(r.status)}>{r.status}</span>
                      </div>
                      <div className="br-list-row-reviewer br-mobile-card-reviewer">
                        <span className="br-mobile-card-meta-icon">
                          <ApplicantIcon />
                        </span>
                        {String(r.reviewer || "").toLowerCase() ===
                        "unassigned" ? (
                          <span className="br-list-row-reviewer-unassigned">
                            {r.reviewer}
                          </span>
                        ) : (
                          <span className="br-list-row-reviewer-text">
                            {r.reviewer}
                          </span>
                        )}
                      </div>
                      <div className="br-list-row-actions br-mobile-card-actions">
                        <div
                          className="br-view br-mobile-card-view"
                          onClick={() => {
                            openView(r);
                            setreqID(r.companyId);
                          }}
                        >
                          <span className="br-eye" aria-hidden="true">
                            <EyeViewOpened />
                          </span>
                          <span>View</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {!loadingList && rows.length === 0 && (
                  <div className="br-empty">No results</div>
                )}
                {loadingList && <div className="br-empty">Loading...</div>}
              </div>

              <div className="nav-bar-container">
                <div className="nav-bar-functionality">
                  <div className="functionality-description">Rows per page</div>
                  <div className="number-of-rows-displayed">
                    <div className="number-of-rows-displayed-container">
                      <input
                        className="number-displayed"
                        type="number"
                        value={RowsPerPage}
                        onChange={handleChangeRows}
                        min={1}
                        max={100}
                      />
                    </div>
                  </div>
                  <div className="over-all-rows">of {totalElements} rows</div>
                </div>

                <div className="nav-table-bar">
                  <div
                    className={`nav-bar-button ${
                      currentPage === 1 ? "disabled" : ""
                    }`}
                    onClick={() => {
                      if (currentPage > 1) setCurrentPage((p) => p - 1);
                    }}
                  >
                    <ArrowBackIcon />
                  </div>

                  <div className="nav-bar-buttons">
                    {getPages().map((item, i) =>
                      item === "ellipsis" ? (
                        <div className="three-dots-nav" key={`e-${i}`}>
                          ...
                        </div>
                      ) : (
                        <div
                          key={item}
                          className={`nav-page-number ${
                            currentPage === item ? "active-page" : ""
                          }`}
                          onClick={() => setCurrentPage(item)}
                        >
                          {item}
                        </div>
                      ),
                    )}
                  </div>

                  <div
                    className={`nav-bar-button ${
                      currentPage === totalPages ? "disabled" : ""
                    }`}
                    onClick={() => {
                      if (currentPage < totalPages)
                        setCurrentPage((p) => p + 1);
                    }}
                  >
                    <ArrowFront />
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <BusinessRequestView
            row={activeView}
            goBack={() => setActiveView(null)}
            jwt={jwt}
            onStatusChange={updateRowStatus}
          />
        )}
      </div>
    </div>
  );
}

function BusinessRequestView({ row, goBack, jwt, onStatusChange }) {
  const [loading, setLoading] = useState(false);
  const [companyDetails, setCompanyDetails] = useState(null);
  const navigate = useNavigate();
  const [isResubmitOpen, setIsResubmitOpen] = useState(false);
  const [isRejectOpen, setIsRejectOpen] = useState(false);
  const [isApproveOpen, setIsApproveOpen] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewImageLoading, setPreviewImageLoading] = useState(false);

  const [actionLoading, setActionLoading] = useState(false);

  const [internalNotes, setInternalNotes] = useState("");
  const [lastSavedNotes, setLastSavedNotes] = useState("");
  const [notesSaving, setNotesSaving] = useState(false);

  const [assignedOn, setAssignedOn] = useState(null);
  const [lastActivity, setLastActivity] = useState(null);

  const [resolvedCompanyId, setResolvedCompanyId] = useState(null);

  const detailBadgeClass = (s) => {
    const v = String(s || "").toLowerCase();
    if (v === "pending") return "br-status br-status-pending";
    if (v === "approved") return "br-status br-status-approved";
    if (v === "resubmit") return "br-status br-status-resubmit";
    if (v === "rejected") return "br-status br-status-rejected";
    return "br-status";
  };

  useEffect(() => {
    if (row?.assignedOn) setAssignedOn(row.assignedOn);
    if (row?.lastActivity) setLastActivity(row.lastActivity);
  }, [row]);

  const [resubmitMsg, setResubmitMsg] = useState(`Dear Applicant,

Thank you for submitting your application to Javelynx. We have reviewed your submission and require additional documentation to proceed with the verification process.

Please re-upload the following documents:
{DOCUMENT_LIST}

Please ensure all documents are clear, legible, and contain all required information. You have 7 days to resubmit these documents.

If you have any questions, please contact our support team.

Best regards,
Javelynx Team`);

  const [rejectReason, setRejectReason] = useState("");
  const [notifyRejectEmail, setNotifyRejectEmail] = useState(true);

  const [docs, setDocs] = useState([
    {
      key: "pcc",
      attachmentId: null,
      dataUrl: "",
      title: "Police Clearance",
      state: "idle",
      issue: "",
    },
    {
      key: "passport",
      attachmentId: null,
      dataUrl: "",
      title: "ID",
      state: "idle",
      issue: "",
    },
    {
      key: "legal",
      attachmentId: null,
      dataUrl: "",
      title: "Proof of Leagality",
      state: "idle",
      issue: "",
    },
    {
      key: "tax",
      attachmentId: null,
      dataUrl: "",
      title: "TAX Number",
      state: "idle",
      issue: "",
    },
  ]);

  const [openFlagKey, setOpenFlagKey] = useState(null);

  const getDocsStorageKey = () => {
    const key =
      pickCompanyIdFromRow(row) ||
      companyDetails?.companyID ||
      companyDetails?.companyId ||
      null;
    if (!key) return null;
    return `br_docs_state_${key}`;
  };

  const loadDocsOverrides = () => {
    try {
      const storageKey = getDocsStorageKey();
      if (!storageKey) return null;
      const raw = localStorage.getItem(storageKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : null;
    } catch (e) {
      return null;
    }
  };

  const persistDocsOverrides = (list) => {
    try {
      const storageKey = getDocsStorageKey();
      if (!storageKey) return;
      const payload = {};
      (list || []).forEach((d) => {
        payload[d.key] = {
          state: d.state,
          issue: d.issue || "",
        };
      });
      localStorage.setItem(storageKey, JSON.stringify(payload));
    } catch (e) {}
  };

  const flagReasons = [
    "Unclear Blurry",
    "Expired Document",
    "Wrong Document Type",
    "Information Mismatch",
    "Incomplete Information",
  ];

  const keyToType = (k) => {
    if (k === "pcc") return "POLICE_CLEARANCE";
    if (k === "passport") return "ID";
    if (k === "legal") return "PROOF_OF_LEAGALITY";
    return null;
  };

  const mapBackendStatusToUiState = (st) => {
    const v = String(st || "").toUpperCase();
    if (v === "APPROVED" || v === "VERIFIED" || v === "VERIFICATION")
      return "verified";
    if (
      v === "UNCLEAR_BLURRY" ||
      v === "EXPIRED_DOCUMENT" ||
      v === "WRONG_DOCUMENT_TYPE" ||
      v === "INFORMATION_MISMATCH" ||
      v === "INCOMPLETE_INFORMATION"
    )
      return "flagged";
    return "idle";
  };

  const mapReasonToBackendStatus = (reason) => {
    const r = String(reason || "")
      .trim()
      .toLowerCase();
    if (r === "unclear blurry") return "UNCLEAR_BLURRY";
    if (r === "expired document") return "EXPIRED_DOCUMENT";
    if (r === "wrong document type") return "WRONG_DOCUMENT_TYPE";
    if (r === "information mismatch") return "INFORMATION_MISMATCH";
    if (r === "incomplete information") return "INCOMPLETE_INFORMATION";
    return "INCOMPLETE_INFORMATION";
  };

  const backendStatusToUiReason = (st) => {
    const v = String(st || "").toUpperCase();
    if (v === "UNCLEAR_BLURRY") return "Unclear Blurry";
    if (v === "EXPIRED_DOCUMENT") return "Expired Document";
    if (v === "WRONG_DOCUMENT_TYPE") return "Wrong Document Type";
    if (v === "INFORMATION_MISMATCH") return "Information Mismatch";
    if (v === "INCOMPLETE_INFORMATION") return "Incomplete Information";
    return "";
  };

  const getFlagLabelForDoc = (docKey) => {
    if (docKey === "pcc") return "Reason";
    return "Issue";
  };

  const getFlagDescription = (issue) => {
    const v = String(issue || "")
      .trim()
      .toLowerCase();
    if (v === "unclear blurry") return "Document is unclear and unreadable";
    if (v === "expired document") return "Document has expired";
    if (v === "wrong document type") return "Wrong document type was uploaded";
    if (v === "information mismatch")
      return "Document information does not match application data";
    if (v === "incomplete information")
      return "Document is missing required information";
    return issue || "Issue";
  };

  const buildFlaggedDocsForMessage = () => {
    const flagged = docs.filter((d) => d.state === "flagged");
    if (!flagged.length) return "No flagged documents.";
    return flagged
      .map(
        (d) =>
          `${d.title}\n${getFlagLabelForDoc(d.key)}: ${getFlagDescription(d.issue)}`,
      )
      .join("\n\n");
  };

  const parseFlaggedDocsFromText = (txt) => {
    const raw = String(txt || "").trim();
    if (!raw) return [];
    const lines = raw
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);

    const out = [];
    for (let i = 0; i < lines.length - 1; i++) {
      const maybeTitle = lines[i];
      const maybeIssue = lines[i + 1];
      if (/^(issue|reason)\s*:/i.test(maybeIssue)) {
        out.push({
          key: `parsed-${i}`,
          title: maybeTitle,
          issueLine: maybeIssue,
        });
      }
    }
    return out;
  };

  const sendAttachmentStatus = async (attachmentId, attachmentStatus) => {
    try {
      if (!jwt || !attachmentId) return false;
      const payload = { attachmentID: attachmentId, attachmentStatus };
      const res = await companyController.updateCompanyAttachmentStatus({
        attachments: payload,
        jwt,
      });
      return res?.status === 200;
    } catch (e) {
      return false;
    }
  };

  const hasFlagged = docs.some((d) => d.state === "flagged");

  useEffect(() => {
    if (loading) return;
    const overrides = loadDocsOverrides();
    if (!overrides) return;
    setDocs((prev) =>
      prev.map((d) => {
        const ov = overrides[d.key];
        if (!ov) return d;
        return {
          ...d,
          state: ov.state || d.state,
          issue: ov.issue || "",
        };
      }),
    );
  }, [row?.id, companyDetails?.companyID, companyDetails?.companyId, loading]);

  useEffect(() => {
    if (!getDocsStorageKey()) return;
    persistDocsOverrides(docs);
  }, [docs, row?.id, companyDetails?.companyID, companyDetails?.companyId]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!e.target.closest(".brd-flag-dd")) setOpenFlagKey(null);
    };
    const onEsc = (e) => {
      if (e.key === "Escape") setOpenFlagKey(null);
    };
    document.addEventListener("click", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("click", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);

  useEffect(() => {
    const onEsc = (e) => {
      if (e.key === "Escape") setPreviewDoc(null);
    };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, []);

  useEffect(() => {
    setPreviewImageLoading(
      previewDoc?.previewType === "image" && Boolean(previewDoc?.dataUrl),
    );
  }, [previewDoc?.previewType, previewDoc?.dataUrl]);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        if (!jwt) {
          toast.error("Missing JWT token");
          return;
        }

        setLoading(true);

        const { companyId: adminCompanyId, businessProfileID } =
          getCompanyIdsFromJwt(jwt);
        const companyIdToLoad = pickCompanyIdFromRow(row) || adminCompanyId;

        if (!companyIdToLoad) {
          toast.error("companyId not found");
          return;
        }
        if (!businessProfileID) {
          toast.error("businessProfileID not found in JWT");
          return;
        }

        const requestId =
          normalizeRequestId(
            pickRequestIdFromRow(row) || row?.requestId || null,
          ) ||
          companyIdToLoad ||
          null;

        const copmayRequest = {
          companyId: companyIdToLoad,
          businessProfileID,
          requestId,
        };

        const res = await companyController.getCompanyDetails({
          copmayRequest,
          Authorization: jwt,
        });
        if (res?.status === 200 && res?.data) {
          setCompanyDetails(res.data);
          const fetchedNotes =
            typeof res.data?.internalNotes === "string"
              ? res.data.internalNotes
              : typeof res.data?.internalNote === "string"
                ? res.data.internalNote
                : "";
          setInternalNotes(fetchedNotes);
          setLastSavedNotes(fetchedNotes);

          const dv =
            res.data?.documentationVerification ??
            res.data?.documentation_verification ??
            res.data?.attachments ??
            [];

          if (Array.isArray(dv)) {
            setDocs((prev) =>
              prev.map((d) => {
                const at = keyToType(d.key);
                if (!at) return d;
                const match =
                  dv.find(
                    (a) =>
                      String(
                        a?.attachmentType ||
                          a?.type ||
                          a?.attachment_type ||
                          "",
                      ).toUpperCase() === String(at || "").toUpperCase(),
                  ) || null;

                if (!match) return d;

                const attachmentId =
                  match?.attachmentID ??
                  match?.attachmentId ??
                  match?.id ??
                  null;
                const dataUrl =
                  match?.dataUrl ??
                  match?.attachmentUrl ??
                  match?.url ??
                  match?.attachment_url ??
                  "";

                const backendStatus =
                  match?.attachmentStatus ??
                  match?.status ??
                  match?.attachment_status ??
                  "DEFAULT";

                const uiState = mapBackendStatusToUiState(backendStatus);
                const issueLabel =
                  uiState === "flagged"
                    ? backendStatusToUiReason(backendStatus)
                    : "";

                return {
                  ...d,
                  attachmentId,
                  dataUrl,
                  state: uiState,
                  issue: issueLabel,
                };
              }),
            );
          }

          setDocs((prev) =>
            prev.map((d) => {
              if (d.key !== "tax") return d;
              const isVerified = Boolean(res.data?.isTaxNumberVerified);
              return {
                ...d,
                attachmentId: null,
                dataUrl: "",
                state: isVerified ? "verified" : "idle",
                issue: "",
              };
            }),
          );
        } else {
          toast.error(
            res?.response?.data?.message || "Company details not found",
          );
        }
      } catch (e) {
        toast.error(
          e?.response?.data?.message || "Error loading company details",
        );
      } finally {
        setLoading(false);
      }
    };

    if (row?.id) fetchDetails();
  }, [row?.id, jwt]);

  const toggleVerifyDoc = async (key) => {
    if (key === "tax") {
      const current = docs.find((d) => d.key === key);
      if (!current) return;

      const prevState = current.state;
      const prevIssue = current.issue;
      const nextVerify = current.state !== "verified";

      setDocs((prev) =>
        prev.map((d) =>
          d.key === key
            ? { ...d, state: nextVerify ? "verified" : "idle", issue: "" }
            : d,
        ),
      );
      setOpenFlagKey(null);

      if (nextVerify) {
        const taxUpdated = await syncTaxNumberIfNumeric();
        if (!taxUpdated) {
          toast.warning("Tax number update skipped, verification saved.");
        }
      }

      const ok = await sendTaxVerificationStatus({ verify: nextVerify });

      if (!ok) {
        setDocs((prev) =>
          prev.map((d) =>
            d.key === key ? { ...d, state: prevState, issue: prevIssue } : d,
          ),
        );
        toast.error("Save failed");
        return;
      }

      setCompanyDetails((prev) =>
        prev
          ? {
              ...prev,
              isTaxNumberVerified: nextVerify,
            }
          : prev,
      );
      return;
    }

    const current = docs.find((d) => d.key === key);
    if (!current?.attachmentId) return toast.error("attachmentID is missing");

    const prevState = current.state;
    const prevIssue = current.issue;

    const isVerified = current.state === "verified";
    const nextStatus = isVerified ? "DEFAULT" : "APPROVED";
    const nextState = isVerified ? "idle" : "verified";

    setDocs((prev) =>
      prev.map((d) =>
        d.key === key ? { ...d, state: nextState, issue: "" } : d,
      ),
    );
    setOpenFlagKey(null);

    const ok = await sendAttachmentStatus(current.attachmentId, nextStatus);

    if (!ok) {
      toast.warning("Update request failed on server. UI kept updated.");
    }
  };

  const openDocumentPreview = (doc) => {
    if (doc?.key === "tax") {
      const taxValue = String(companyDetails?.taxNumber ?? "").trim();
      if (!taxValue) {
        toast.info("No tax number found for this company");
        return;
      }
      setPreviewDoc({
        key: doc.key,
        title: doc.title,
        previewType: "text",
        value: taxValue,
      });
      return;
    }

    if (!doc?.dataUrl) {
      toast.info(`No file found for ${doc?.title || "this document"}`);
      return;
    }
    setPreviewDoc({
      ...doc,
      previewType: "image",
    });
  };

  const setFlagReason = async (key, reason) => {
    if (key === "tax") {
      setDocs((prev) =>
        prev.map((d) =>
          d.key === key ? { ...d, state: "flagged", issue: reason } : d,
        ),
      );
      setOpenFlagKey(null);
      return;
    }

    const current = docs.find((d) => d.key === key);
    if (!current?.attachmentId) return toast.error("attachmentID is missing");

    const prevState = current.state;
    const prevIssue = current.issue;

    const backendEnum = mapReasonToBackendStatus(reason);

    setDocs((prev) =>
      prev.map((d) =>
        d.key === key ? { ...d, state: "flagged", issue: reason } : d,
      ),
    );
    setOpenFlagKey(null);

    const ok = await sendAttachmentStatus(current.attachmentId, backendEnum);

    if (!ok) {
      toast.warning("Update request failed on server. UI kept updated.");
    }
  };

  const onFlagButton = async (key) => {
    if (key === "tax") {
      const current = docs.find((d) => d.key === key);
      if (!current) return;

      if (current.state === "flagged") {
        setDocs((prev) =>
          prev.map((d) =>
            d.key === key ? { ...d, state: "idle", issue: "" } : d,
          ),
        );
        setOpenFlagKey(null);
        return;
      }

      setOpenFlagKey((p) => (p === key ? null : key));
      return;
    }

    const current = docs.find((d) => d.key === key);
    if (!current) return;

    if (!current.attachmentId) {
      toast.error("attachmentID is missing");
      return;
    }

    if (current.state === "flagged") {
      const prevState = current.state;
      const prevIssue = current.issue;

      setDocs((prev) =>
        prev.map((d) =>
          d.key === key ? { ...d, state: "idle", issue: "" } : d,
        ),
      );
      setOpenFlagKey(null);

      const ok = await sendAttachmentStatus(current.attachmentId, "DEFAULT");
      if (!ok) {
        toast.warning("Update request failed on server. UI kept updated.");
      }
      return;
    }

    setOpenFlagKey((p) => (p === key ? null : key));
  };

  const getCompanyIDForAction = () => {
    return (
      pickCompanyIdFromRow(row) ||
      companyDetails?.companyID ||
      companyDetails?.companyId ||
      getCompanyIdsFromJwt(jwt).companyId ||
      null
    );
  };

  const saveInternalNotes = async () => {
    const nextNotes = String(internalNotes ?? "");
    if (notesSaving) return;
    if (nextNotes === lastSavedNotes) return;

    const companyID = getCompanyIDForAction();
    if (!companyID) {
      toast.error("companyID missing");
      return;
    }

    setNotesSaving(true);
    try {
      const payloads = [
        { companyID, internalNotes: nextNotes },
        { companyId: companyID, internalNotes: nextNotes },
        { companyID, internalNote: nextNotes },
        { companyId: companyID, internalNote: nextNotes },
      ];

      let saved = false;
      let lastError = null;

      for (const updateDetails of payloads) {
        try {
          const res = await companyController.updatecompanydetails({
            updateDetails,
            jwt,
          });
          if (Utils.isSuccessResponse(res)) {
            saved = true;
            break;
          }
        } catch (e) {
          lastError = e;
        }
      }

      if (!saved) throw lastError || new Error("Failed to save notes");

      setLastSavedNotes(nextNotes);
      setCompanyDetails((prev) =>
        prev
          ? {
              ...prev,
              internalNotes: nextNotes,
            }
          : prev,
      );
      toast.success("Internal notes saved");
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Save failed");
    } finally {
      setNotesSaving(false);
    }
  };

  const sendTaxVerificationStatus = async ({ verify }) => {
    try {
      const companyId = getCompanyIDForAction();
      if (!jwt || !companyId) return false;

      const res = await companyController.toggleVerifyTaxNumber({
        request: {
          companyId,
          verify: Boolean(verify),
        },
        jwt,
      });
      return Utils.isSuccessResponse(res);
    } catch (e) {
      return false;
    }
  };

  const syncTaxNumberIfNumeric = async () => {
    try {
      const companyId = getCompanyIDForAction();
      const rawTax = companyDetails?.taxNumber;
      if (!jwt || !companyId) return true;
      if (
        rawTax === null ||
        rawTax === undefined ||
        String(rawTax).trim() === ""
      )
        return true;

      const taxRaw = String(rawTax).trim();
      const parsed = Number(taxRaw);
      const taxNumberValue = Number.isFinite(parsed) ? parsed : taxRaw;

      const payloads = [
        { companyId, taxNumber: taxNumberValue },
        { companyID: companyId, taxNumber: taxNumberValue },
      ];

      for (const updateTaxNb of payloads) {
        try {
          const res = await companyController.updateTaxNumber({
            updateTaxNb,
            jwt,
          });
          if (Utils.isSuccessResponse(res)) return true;
        } catch (e) {}
      }

      return false;
    } catch (e) {
      return false;
    }
  };

  const getOwnerIDForAction = () => {
    return (
      row?.ownerId ||
      row?.ownerID ||
      row?.raw?.ownerId ||
      row?.raw?.ownerID ||
      row?.raw?.applicantID ||
      row?.raw?.applicantId ||
      companyDetails?.ownerId ||
      companyDetails?.ownerID ||
      companyDetails?.owner?.id ||
      companyDetails?.applicantID ||
      companyDetails?.applicantId ||
      null
    );
  };
  const buildCompanyAttachmentReasons = (popupFlaggedDocs) => {
    return popupFlaggedDocs.reduce((acc, item) => {
      if (item?.title && item?.issue) {
        const enumKey = item.title.trim().toUpperCase().replace(/\s+/g, "_");
        const enumValue = item.issue.trim().toUpperCase().replace(/\s+/g, "_");
        acc[enumKey] = enumValue;
      }
      return acc;
    }, {});
  };
  const buildStatusPayload = ({ companyStatus, messageToApplicant }) => {
    const companyID = getCompanyIDForAction();
    const ownerId = getOwnerIDForAction();

    if (!companyID) throw new Error("companyID missing");
    if (!ownerId) throw new Error("ownerId missing");

    return {
      companyAttachmentReasons: buildCompanyAttachmentReasons(popupFlaggedDocs),
      companyID,
      companyStatus,
      messageToApplicant,
      ownerId,
      requestId: companyID,
      requestType: "VENDOR",
      url: "test.com",
    };
  };

  const approveAllAttachments = async () => {
    const ids = docs
      .filter((d) => d.key !== "tax")
      .map((d) => d?.attachmentId)
      .filter((id) => typeof id === "string" && id.trim() !== "");

    if (!ids.length) return;

    for (const attachmentID of ids) {
      const res = await companyController.updateCompanyAttachmentStatus({
        attachments: {
          attachmentID,
          attachmentStatus: "APPROVED",
        },
        jwt,
      });
      if (!Utils.isSuccessResponse(res)) {
        throw new Error(
          res?.response?.data?.message || "Failed to update attachment status",
        );
      }
    }
  };

  const isConflict409 = (err) => (err?.status ?? err?.response?.status) === 409;
  const isBadRequest400 = (err) =>
    (err?.status ?? err?.response?.status) === 400;

  const getCompanyEmailForAction = () => {
    return (
      row?.submittedBy ||
      row?.raw?.email ||
      row?.raw?.submittedBy ||
      companyDetails?.email ||
      companyDetails?.companyEmail ||
      companyDetails?.ownerEmail ||
      null
    );
  };

  useEffect(() => {
    setResolvedCompanyId(getCompanyIDForAction());
  }, [row, companyDetails, jwt]);

  const approveRequest = async () => {
    try {
      // if (!jwt) return toast.error("Missing JWT token");
      if (loading) return toast.error("Wait for details to load");

      setActionLoading(true);
      saveInternalNotes();
      const approvePayload = buildStatusPayload({
        companyStatus: "APPROVED",
        messageToApplicant: "Your business request has been approved.",
      });
      await approveAllAttachments();
      const taxVerifiedOnApprove = await sendTaxVerificationStatus({
        verify: true,
      });
      if (!taxVerifiedOnApprove) {
        toast.warning("Approved, but tax verification API did not confirm.");
      }

      if (!companyController.sendapprovedemail) {
        throw new Error("approvedEmail endpoint is not configured");
      }

      try {
        const approveEmailResponse = await companyController.sendapprovedemail({
          statusPayload: approvePayload,
          jwt,
        });
        if (!Utils.isSuccessResponse(approveEmailResponse)) {
          if (isConflict409(approveEmailResponse)) {
            toast.info("Approval email already sent");
            return;
          }
          throw approveEmailResponse;
        }
      } catch (e) {
        if (!isConflict409(e)) throw e;
        toast.info("Approval email already sent");
        return;
      }

      onStatusChange?.(row?.id, "Approved");
      setDocs((prev) =>
        prev.map((d) => ({
          ...d,
          state: "verified",
          issue: "",
        })),
      );
      setCompanyDetails((prev) =>
        prev
          ? {
              ...prev,
              companyStatus: "APPROVED",
              isTaxNumberVerified: true,
            }
          : prev,
      );

      setLastActivity(new Date().toISOString());
      setIsApproveOpen(true);
      toast.success("Approved");
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Approve failed");
    } finally {
      setActionLoading(false);
    }
  };

  const resubmitRequest = async () => {
    try {
      if (!hasFlagged) {
        return toast.error("Flag at least one document first.");
      }

      setActionLoading(true);
      saveInternalNotes();
      const baseMsg = String(resubmitMsg || "").trim();
      const flaggedDocsText = buildFlaggedDocsForMessage();

      const finalMsg = baseMsg.includes("{DOCUMENT_LIST}")
        ? baseMsg.replace("{DOCUMENT_LIST}", flaggedDocsText)
        : `${baseMsg}\n\nFlagged Required Documents:\n${flaggedDocsText}`;

      if (!companyController.sendResubmissionEmail) {
        throw new Error("resubmissionEmail endpoint is not configured");
      }

      const statusPayload = buildStatusPayload({
        companyStatus: "RESUBMIT",
        messageToApplicant: finalMsg,
      });

      const resubmissionResponse = await companyController.sendResubmissionEmail({
        companyEmail: statusPayload,
        Authorization: userService.getUser(),
      });
      if (!Utils.isSuccessResponse(resubmissionResponse)) {
        if (isConflict409(resubmissionResponse)) {
          toast.info("Resubmission email already sent");
          return;
        }
        throw resubmissionResponse;
      }

      onStatusChange?.(row?.id, "Resubmit");
      setLastActivity(new Date().toISOString());
      toast.success("Resubmission sent");
      setIsResubmitOpen(false);
      goBack();
    } catch (e) {
      if (isConflict409(e)) {
        toast.info("Resubmission email already sent");
        return;
      }

      toast.error(
        e?.response?.data?.message || e?.message || "Resubmission failed",
      );
    } finally {
      setActionLoading(false);
    }
  };
  const rejectRequest = async () => {
    try {
      if (loading) return toast.error("Wait for details to load");

      const reason = String(rejectReason || "").trim();
      if (!reason) return toast.error("Please enter a rejection reason.");

      setActionLoading(true);
      saveInternalNotes();
      const rejectPayload = buildStatusPayload({
        companyStatus: "REJECTED",
        messageToApplicant: reason,
      });

      if (notifyRejectEmail && !companyController.sendrejectedemail) {
        throw new Error("rejectedEmail endpoint is not configured");
      }

      let rejectResponse = null;
      if (notifyRejectEmail) {
        try {
          rejectResponse = await companyController.sendrejectedemail({
            companyEmail: rejectPayload,
            Authorization: userService.getUser(),
          });
          if (!Utils.isSuccessResponse(rejectResponse)) {
            throw rejectResponse;
          }
        } catch (e) {
          if (isConflict409(e)) {
            toast.info("Rejection email already sent");
            return;
          } else if (isBadRequest400(e)) {
            const email = String(getCompanyEmailForAction() || "").trim();
            if (!email) throw e;
            rejectResponse = await companyController.sendrejectedemail({
              statusPayload: {
                companyEmail: email,
                reason,
              },
              jwt,
            });
            if (!Utils.isSuccessResponse(rejectResponse)) {
              throw rejectResponse;
            }
          } else {
            throw e;
          }
        }
      }

      if (notifyRejectEmail && !Utils.isSuccessResponse(rejectResponse)) {
        throw new Error("Reject failed");
      }

      onStatusChange?.(row?.id, "Rejected");
      setLastActivity(new Date().toISOString());
      if (Utils.isSuccessResponse(rejectResponse)) {
        toast.success("Rejected");
      }
      goBack();
      setIsRejectOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Reject failed");
    } finally {
      setActionLoading(false);
    }
  };

  const verifiedCount = docs.filter((d) => d.state === "verified").length;
  const flaggedCount = docs.filter((d) => d.state === "flagged").length;
  const flaggedDocs = docs.filter((d) => d.state === "flagged");
  const fallbackFlaggedDocs = parseFlaggedDocsFromText(
    companyDetails?.messageToApplicant ||
      row?.raw?.messageToApplicant ||
      row?.raw?.message ||
      "",
  );
  const fallbackFromDraft = parseFlaggedDocsFromText(resubmitMsg);
  const popupFlaggedDocs = flaggedDocs.length
    ? flaggedDocs
    : fallbackFlaggedDocs.length
      ? fallbackFlaggedDocs
      : fallbackFromDraft;

  const companyIdLabel = resolvedCompanyId || "-";
  const requestTypeLabel = row?.type || "-";
  const statusLabel = row?.status || "-";

  const typeApi = mapUiTypeToApi(row?.type);
  const isIndependentDriver =
    String(typeApi || "").toUpperCase() === "INDEVIDUAL_DRIVER" ||
    String(row?.type || "").toLowerCase() === "independent driver";

  const applicantFields = isIndependentDriver
    ? [
        { k: "Driver Name", v: row?.name || "-" },
        { k: "Business Type", v: asText(companyDetails?.businessType) || "-" },
        {
          k: "Country",
          v:
            pickCountryFromDetails(companyDetails) ||
            asText(row?.country) ||
            "-",
        },
        {
          k: "Phone Number",
          v: asText(companyDetails?.phoneNumber) || "+961 x xxxxxx",
        },
        { k: "Email Address", v: row?.submittedBy || "-" },
        { k: "Submission Date", v: row?.date || "-" },
      ]
    : [
        {
          k: "Company Name",
          v: asText(companyDetails?.companyName || row?.companyName) || "-",
        },
        { k: "Business Type", v: asText(companyDetails?.businessType) || "-" },
        { k: "Owner Name", v: row?.name || "-" },
        {
          k: "Country",
          v:
            pickCountryFromDetails(companyDetails) ||
            asText(row?.country) ||
            "-",
        },
        { k: "Email Address", v: row?.submittedBy || "-" },
        {
          k: "Phone Number",
          v: asText(companyDetails?.phoneNumber) || "+961 x xxxxxx",
        },
        { k: "TAX Number", v: asText(companyDetails?.taxNumber) || "-" },
        { k: "Submission Date", v: row?.date || "-" },
      ];

  const canConfirmResubmit = !loading;
  useEffect(() => {
    if (isResubmitOpen || isRejectOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isResubmitOpen, isRejectOpen]);
  return (
    <div className="brd-container">
      <div className="brd-topbar">
        <div className="brd-left">
          <div className="brd-pills">
            <div className="brd-pill-label">Request ID</div>
            <div className="brd-pill-value">{companyIdLabel}</div>
          </div>
          <div className="brd-pill-cntnr">
            <div className="brd-pill">
              <div className="brd-pill-label">Request Type</div>
              <div className="brd-pill-value">{requestTypeLabel}</div>
            </div>
            <div className="brd-pill">
              <div className="brd-pill-label">Status</div>
              <div className="brd-pill-value">
                <span className={detailBadgeClass(statusLabel)}>
                  {statusLabel}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="brd-actions">
          <button
            className="brd-btn brd-btn-ghost"
            onClick={() => setIsResubmitOpen(true)}
            disabled={actionLoading || loading}
          >
            <ResubmitIcon />
            Resubmit
          </button>

          <button
            className="brd-btn brd-btn-danger"
            onClick={() => setIsRejectOpen(true)}
            disabled={actionLoading || loading}
          >
            <RejectedIcon />
            Reject
          </button>

          <button
            className="brd-btn brd-btn-success"
            onClick={approveRequest}
            disabled={actionLoading || loading}
          >
            <Approved />
            {actionLoading ? "Approving..." : "Approve"}
          </button>
        </div>
      </div>

      {loading && <div className="br-empty">Loading details...</div>}

      <div className="brd-grid">
        <div className="brd-main">
          <div className="brd-card-info">
            <div className="brd-card-title-info">
              <ApplicantIcon />
              Applicant Information
            </div>

            <div className="brd-info">
              {applicantFields.map((f) => (
                <div className="brd-info-item" key={f.k}>
                  <div className="brd-k">{f.k}</div>
                  <div className="brd-v">{f.v}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="brd-card brd-card-docs">
            <div className="brd-card-title brd-card-title-row">
              <div className="brd-card-title-left">
                <DocumentsVerificationIcon />
                Documents Verification
              </div>

              <div className="brd-doc-stats">
                <div className="brd-stat">
                  <span className="brd-dot brd-dot-green" />
                  Verified: {verifiedCount}
                </div>
                <div className="brd-stat">
                  <span className="brd-dot brd-dot-red" />
                  Flagged: {flaggedCount}
                </div>
              </div>
            </div>

            <div className="brd-docs-grid">
              {docs.map((doc) => (
                <div
                  key={doc.key}
                  className={`brd-doc-card ${
                    doc.state === "verified"
                      ? "brd-doc-verified"
                      : doc.state === "flagged"
                        ? "brd-doc-flagged"
                        : ""
                  }`}
                >
                  <div className="brd-doc-top">
                    <div className="brd-doc-title2">{doc.title}</div>

                    {doc.state === "flagged" && (
                      <div className="brd-issue">
                        <span className="brd-issue-dot" />
                        <Exclamation />
                        Issue:<br></br> {doc.issue}
                      </div>
                    )}
                  </div>

                  <div className="brd-doc-actions2">
                    <button
                      className="brd-act"
                      onClick={() => openDocumentPreview(doc)}
                    >
                      <span className="brd-act-ic">
                        <EyeViewOpened />
                      </span>
                      View
                    </button>

                    <button
                      className={`brd-act ${
                        doc.state === "verified" ? "brd-act-verified" : ""
                      }`}
                      onClick={() => toggleVerifyDoc(doc.key)}
                    >
                      <span className="brd-act-ic">
                        {doc.state === "verified" ? (
                          <Approved />
                        ) : (
                          <CyrcleIcon />
                        )}
                      </span>
                      {doc.state === "verified" ? "Verified" : "Verify"}
                    </button>

                    <div className="brd-flag-dd">
                      <button
                        className={`brd-act ${
                          doc.state === "flagged" ? "brd-act-flagged" : ""
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onFlagButton(doc.key);
                        }}
                      >
                        <span className="brd-act-ic">
                          <FlagIcon />
                        </span>
                        Flag
                      </button>

                      {openFlagKey === doc.key && (
                        <div
                          className="brd-flag-menu"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {flagReasons.map((r) => (
                            <button
                              key={r}
                              className={`brd-flag-item ${
                                r === "Expired Document"
                                  ? "brd-flag-item-danger"
                                  : ""
                              } ${
                                doc.state === "flagged" && doc.issue === r
                                  ? "brd-flag-item-active"
                                  : ""
                              }`}
                              onClick={() => {
                                setFlagReason(doc.key, r);
                              }}
                            >
                              {r}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="brd-side">
          <div className="brd-card-internal">
            <div className="brd-card-title">
              <InternalNotesIcon />
              Internal Notes
            </div>

            <textarea
              className="brd-box"
              value={internalNotes}
              onChange={(e) => setInternalNotes(e.target.value)}
              // onBlur={saveInternalNotes}
              placeholder="Write internal notes here... (Only visible to admins)"
              spellCheck={false}
              disabled={notesSaving}
            />

            <div className="brd-hint">Only visible to admins</div>
          </div>

          <div className="brd-card-internal">
            <div className="brd-card-title">
              <ReviewActiveLog />
              Review Activity Log
            </div>

            <div className="brd-review-box">
              <div className="brd-review-row">
                <div>
                  <div className="brd-k">Reviewer</div>
                  <div className="brd-v2">{row?.reviewer || "Unassigned"}</div>
                </div>

                <div>
                  <div className="brd-k">Assigned On</div>
                  <div className="brd-v2">
                    {assignedOn
                      ? new Date(assignedOn).toLocaleString("en-GB")
                      : "-"}
                  </div>
                </div>
              </div>

              <div className="brd-review-row">
                <div>
                  <div className="brd-k">Last Activity</div>
                  <div className="brd-v2">
                    {lastActivity
                      ? new Date(lastActivity).toLocaleString("en-GB")
                      : "-"}
                  </div>
                </div>

                <div>
                  <div className="brd-k">Current Status</div>
                  <div className="brd-v2">
                    <span className={detailBadgeClass(row?.status || "-")}>
                      {row?.status || "-"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="brd-hint">Only visible to admins</div>
          </div>
        </div>
      </div>

      {isApproveOpen &&
        createPortal(
          <div
            className="popup-approved-overlay"
            onClick={() => setIsApproveOpen(false)}
          >
            <div
              className="popup-approved-container"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="popup-icon-wrap">
                <div className="popup-icon popup-icon-success">
                  <ApprovedBigIcon />
                </div>
              </div>

              <div className="popup-approved-title">Request Approved</div>

              <div className="popup-approved-subtitle">
                This business has been successfully approved and is now active
                on the Javelynx platform. An email notification has been sent,
                and the business can begin operations immediately.
              </div>

              <div className="popup-approved-footer">
                <GloablButton
                  width="285px"
                  height="44px"
                  backgroundColor="#4072C8"
                  text="Go to Business Requests Page"
                  borderRadius="50px"
                  padding="11.0527px 18.4211px"
                  boxShadow="inset 0px -1px 3px rgba(0, 18, 71, 0.5)"
                  onClick={() => {
                    setIsApproveOpen(false);
                    goBack();
                    window.history.back();
                  }}
                />
              </div>
            </div>
          </div>,
          document.body,
        )}

      {previewDoc &&
        createPortal(
          <div
            className="brd-preview-overlay"
            onClick={() => setPreviewDoc(null)}
          >
            <div
              className="brd-preview-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="brd-preview-header">
                <div className="brd-preview-title">{previewDoc.title}</div>
                <button
                  className="brd-preview-close"
                  onClick={() => setPreviewDoc(null)}
                  aria-label="Close document preview"
                >
                  ×
                </button>
              </div>

              <div className="brd-preview-body">
                {previewDoc.previewType === "text" ? (
                  <div className="brd-preview-text-card">
                    <div className="brd-preview-text-label">Tax Number</div>
                    <div className="brd-preview-text-value">
                      {previewDoc.value}
                    </div>
                  </div>
                ) : (
                  <div
                    className={`brd-preview-image-shell ${
                      previewImageLoading ? "is-loading" : ""
                    }`}
                  >
                    {previewImageLoading ? (
                      <InlineLogoLoader
                        className="brd-preview-image-loader"
                        logoClassName="brd-preview-image-logo-loader"
                      />
                    ) : null}
                    <img
                      className="brd-preview-image"
                      src={previewDoc.dataUrl}
                      alt={previewDoc.title}
                      onLoad={() => setPreviewImageLoading(false)}
                      onError={() => setPreviewImageLoading(false)}
                    />
                  </div>
                )}
              </div>

              {/* {previewDoc.previewType === "image" && (
                <div className="brd-preview-footer">
                  <a
                    className="brd-preview-link"
                    href={previewDoc.dataUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open original
                  </a>
                </div>
              )} */}
            </div>
          </div>,
          document.body,
        )}

      {isResubmitOpen &&
        createPortal(
          <div
            className="popup-resubmit-overlay"
            onClick={() => setIsResubmitOpen(false)}
          >
            <div
              className="popup-resubmit-container"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="popup-icon-wrap">
                <div className="popup-resubmit-icon">
                  <CyrcleIconRed />
                </div>
              </div>

              <div className="popup-resubmit-title">Request Resubmission</div>

              <div className="popup-resubmit-subtitle">
                The applicant will receive this message and have 7 days to
                resubmit the requested documents.
              </div>

              <div className="popup-resubmit-flagged">
                <div className="popup-flagged-title">
                  Flagged Required Documents:
                </div>

                {popupFlaggedDocs.length === 0 ? (
                  <div className="br-muted">No flagged documents.</div>
                ) : (
                  popupFlaggedDocs.map((d) => (
                    <div className="popup-flagged-item" key={d.key}>
                      <div className="popup-doc-name">{d.title}</div>
                      <div className="popup-doc-reason-issue">
                        {d.issueLine ||
                          `${getFlagLabelForDoc(d.key)}: ${getFlagDescription(d.issue)}`}
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="popup-message">
                <div className="popup-message-label">
                  <MessageIcon />
                  Message to Applicant
                </div>

                <textarea
                  className="popup-textarea"
                  spellCheck={false}
                  value={resubmitMsg}
                  onChange={(e) => setResubmitMsg(e.target.value)}
                />
              </div>

              <div className="popup-resubmit-footer">
                <GloablButton
                  width="182px"
                  height="44px"
                  backgroundColor="#4072C8"
                  text={actionLoading ? "Sending..." : "Confirm & Send"}
                  borderRadius="50px"
                  padding="11.0527px 18.4211px"
                  boxShadow="inset 0px -1px 3px rgba(0, 18, 71, 0.5)"
                  onClick={() => {
                    resubmitRequest();
                  }}
                  disabled={
                    actionLoading ||
                    !hasFlagged ||
                    loading ||
                    !canConfirmResubmit
                  }
                />

                <GloablButton
                  width="115px"
                  height="44px"
                  backgroundColor="#FFFFFF"
                  text="Cancel"
                  color="#344054"
                  borderRadius="50px"
                  padding="11.0527px 18.4211px"
                  boxShadow="inset 0px -1px 3px rgba(0, 18, 71, 0.5)"
                  onClick={() => setIsResubmitOpen(false)}
                  disabled={actionLoading}
                />
              </div>
            </div>
          </div>,
          document.body,
        )}

      {isRejectOpen &&
        createPortal(
          <div
            className="popup-rejected-overlay"
            onClick={() => setIsRejectOpen(false)}
          >
            <div
              className="popup-rejected-container"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="popup-icon-wrap">
                <div className="popup-icon popup-icon-danger">
                  <TrachIcon />
                </div>
              </div>

              <div className="popup-rejected-title">Reject Request</div>

              <div className="popup-rejected-subtitle">
                You are about to reject the application form.
              </div>

              <div className="popup-reject-info">
                <span>Request ID: {companyIdLabel}</span>
                <span>Request Type: {requestTypeLabel}</span>
              </div>

              <div className="popup-reject-warning">
                <Warnig /> This action cannot be undone. The applicant will be
                notified of the rejection and will need to reply if they wish to
                continue.
              </div>

              <div className="popup-reject-reason-card">
                <label className="popup-reject-label">
                  <FaulseIcon />
                  <span>Reason for Rejection </span>
                </label>

                <textarea
                  className="popup-rejectedtextarea"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Provide a detailed reason for rejecting this application..."
                />
              </div>

              <div className="popup-reject-checkbox">
                <input
                  type="checkbox"
                  checked={notifyRejectEmail}
                  onChange={(e) => setNotifyRejectEmail(e.target.checked)}
                />
                <span>Notify applicant of rejection via email</span>
              </div>

              <div className="popup-resubmit-footer">
                <GloablButton
                  width="155px"
                  height="44px"
                  backgroundColor="#F44336"
                  text={actionLoading ? "Rejecting..." : "Confirm Rejection"}
                  borderRadius="50px"
                  padding="11.0527px 18.4211px"
                  gap="7.37px"
                  boxShadow="inset 0px -1px 3px rgba(0, 18, 71, 0.5)"
                  onClick={rejectRequest}
                  disabled={actionLoading || loading}
                />

                <GloablButton
                  width="90px"
                  height="44px"
                  backgroundColor="#FFFFFF"
                  text="Cancel"
                  color="#344054"
                  borderRadius="50px"
                  padding="11.0527px 18.4211px"
                  gap="7.37px"
                  boxShadow="inset 0px -1px 3px rgba(0, 18, 71, 0.5)"
                  onClick={() => setIsRejectOpen(false)}
                  disabled={actionLoading}
                />
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

export default BussinessReq;
